import { ArrowDown, ArrowUp, ChevronRight, Copy, Plus, Trash2, X } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { cn } from '@/components/ui/utils'

import { describeRef } from '../describe'
import { useEditor, useIssues } from './context'
import { formatValue, parseValue } from './doc'
import type { Issue } from './issues'
import { eventOptions, refOptions, valueOptions } from './refs'

/**
 * The manual editor's building blocks. Each field carries its document path in
 * `data-field-path` and shows the issues found there (the summary list scrolls to it), with the
 * three guidance layers of a form: label, helper text, and the checker's messages in place.
 */

export const SELECT_CLASS =
  'h-9 w-full min-w-0 rounded-xl border border-line bg-paper px-2 text-sm text-ink hover:border-line-strong focus-visible:focus-ring aria-[invalid=true]:border-danger'

/* -------------------------------------------------------------------------- */
/* Shells                                                                      */
/* -------------------------------------------------------------------------- */

export interface ControlProps {
  id: string
  invalid: boolean
  describedBy: string | undefined
}

function Messages({ id, errors, warnings, hint }: { id: string; errors: Issue[]; warnings: Issue[]; hint?: ReactNode }) {
  if (errors.length === 0 && warnings.length === 0 && !hint) return null
  return (
    <div id={id} className="flex flex-col gap-0.5">
      {errors.map((e, i) => (
        <p key={`e${i}:${e.message}`} className="text-xs text-danger" data-testid="field-error" data-path={e.path}>
          {e.message}
        </p>
      ))}
      {warnings.map((w, i) => (
        <p key={`w${i}:${w.message}`} className="text-xs text-warn" data-testid="field-warning" data-path={w.path}>
          {w.message}
        </p>
      ))}
      {errors.length === 0 && hint ? <p className="text-xs text-ink-3">{hint}</p> : null}
    </div>
  )
}

export interface EditFieldProps {
  /** The document path this field edits (dotted: `roles.thrower.types`). */
  path: string
  label: string
  hint?: ReactNode
  /** Also show the issues under `path` (default: yes — a leaf field owns its sub-paths). */
  deep?: boolean
  hideLabel?: boolean
  className?: string
  children(ctrl: ControlProps): ReactNode
}

/** One labelled control. */
export function EditField({ path, label, hint, deep = true, hideLabel = false, className, children }: EditFieldProps) {
  const id = useId()
  const { errors, warnings } = useIssues(path, deep)
  const msgId = `${id}-msg`
  const described = errors.length > 0 || warnings.length > 0 || !!hint ? msgId : undefined
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)} data-field-path={path} data-invalid={errors.length > 0 || undefined}>
      <label htmlFor={id} className={cn('text-sm font-medium text-ink', hideLabel && 'sr-only')}>
        {label}
      </label>
      {children({ id, invalid: errors.length > 0, describedBy: described })}
      <Messages id={msgId} errors={errors} warnings={warnings} hint={hint} />
    </div>
  )
}

/** A labelled group of controls (chips, a row of numbers) — a `fieldset`. */
export function EditGroup({ path, label, hint, deep = true, className, children }: { path: string; label: string; hint?: ReactNode; deep?: boolean; className?: string; children: ReactNode }) {
  const id = useId()
  const { errors, warnings } = useIssues(path, deep)
  return (
    <fieldset className={cn('flex min-w-0 flex-col gap-1.5', className)} data-field-path={path} data-invalid={errors.length > 0 || undefined} aria-describedby={`${id}-msg`}>
      <legend className="mb-1.5 text-sm font-medium text-ink">{label}</legend>
      {children}
      <Messages id={`${id}-msg`} errors={errors} warnings={warnings} hint={hint} />
    </fieldset>
  )
}

/** A card of the form: a heading, one line of help, the issues found at its own path. */
export function SectionCard({ path, title, help, actions, children, testId }: { path: string; title: string; help?: ReactNode; actions?: ReactNode; children: ReactNode; testId?: string }) {
  const id = useId()
  const { errors, warnings } = useIssues(path, false)
  return (
    <section className="card flex min-w-0 flex-col gap-4 p-5" data-field-path={path} aria-labelledby={id} data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={id} className="text-lg font-semibold text-ink">
            {title}
          </h2>
          {help ? <p className="text-sm text-ink-2">{help}</p> : null}
        </div>
        {actions}
      </div>
      <Messages id={`${id}-msg`} errors={errors} warnings={warnings} />
      {children}
    </section>
  )
}

/** A sub-card inside a section (one role, one step, one check). */
export function ItemCard({ path, title, controls, children, className, testId }: { path: string; title: ReactNode; controls?: ReactNode; children: ReactNode; className?: string; testId?: string }) {
  const { errors, warnings } = useIssues(path, false)
  const id = useId()
  return (
    <div className={cn('flex min-w-0 flex-col gap-3 rounded-2xl border border-line bg-paper p-4', className)} data-field-path={path} data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-sm font-semibold text-ink">{title}</div>
        {controls}
      </div>
      <Messages id={`${id}-msg`} errors={errors} warnings={warnings} />
      {children}
    </div>
  )
}

/** A folded part ("More options"); opens by itself when something inside needs fixing. */
export function Disclosure({ label, open: forceOpen = false, children, className }: { label: string; open?: boolean; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(forceOpen)
  useEffect(() => {
    if (forceOpen) setOpen(true)
  }, [forceOpen])
  const id = useId()
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <button type="button" className="inline-flex items-center gap-1 self-start rounded-lg text-sm font-medium text-ink-2 hover:text-ink focus-visible:focus-ring" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} aria-hidden />
        {label}
      </button>
      <div id={id} hidden={!open} className="flex flex-col gap-3">
        {open ? children : null}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Buttons                                                                     */
/* -------------------------------------------------------------------------- */

export function AddButton({ children, onClick, disabled, testId }: { children: ReactNode; onClick(): void; disabled?: boolean; testId?: string }) {
  return (
    <Button variant="secondary" size="sm" onClick={onClick} disabled={disabled} className="self-start" data-testid={testId}>
      <Plus aria-hidden />
      {children}
    </Button>
  )
}

/** Up / down / duplicate / remove for one item of a list; `name` makes the labels unique. */
export function ListControls({
  name,
  index,
  count,
  onMove,
  onDuplicate,
  onRemove,
  removeDisabled,
}: {
  name: string
  index: number
  count: number
  onMove?(by: -1 | 1): void
  onDuplicate?(): void
  onRemove?(): void
  removeDisabled?: boolean
}) {
  const icon = 'size-8 [&_svg]:size-4'
  return (
    <div className="flex items-center gap-0.5">
      {onMove ? (
        <>
          <Button variant="ghost" size="icon" className={icon} aria-label={`Move ${name} up`} disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp aria-hidden />
          </Button>
          <Button variant="ghost" size="icon" className={icon} aria-label={`Move ${name} down`} disabled={index >= count - 1} onClick={() => onMove(1)}>
            <ArrowDown aria-hidden />
          </Button>
        </>
      ) : null}
      {onDuplicate ? (
        <Button variant="ghost" size="icon" className={icon} aria-label={`Duplicate ${name}`} onClick={onDuplicate}>
          <Copy aria-hidden />
        </Button>
      ) : null}
      {onRemove ? (
        <Button variant="ghost" size="icon" className={cn(icon, 'hover:text-danger')} aria-label={`Remove ${name}`} disabled={removeDisabled} onClick={onRemove}>
          <Trash2 aria-hidden />
        </Button>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Controls                                                                    */
/* -------------------------------------------------------------------------- */

export function TextInput({ ctrl, value, onChange, placeholder, className, mono = false }: { ctrl: ControlProps; value: unknown; onChange(v: string): void; placeholder?: string; className?: string; mono?: boolean }) {
  return (
    <Input
      id={ctrl.id}
      fieldSize="sm"
      value={typeof value === 'string' ? value : value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-invalid={ctrl.invalid || undefined}
      aria-describedby={ctrl.describedBy}
      className={cn(mono && 'font-mono', className)}
      autoComplete="off"
    />
  )
}

/** A number box that lets you type freely ("0.", "-") and reports a number or `undefined` (empty). */
export function NumberInput({
  ctrl,
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  className,
}: {
  ctrl: ControlProps
  value: unknown
  onChange(v: number | undefined): void
  min?: number
  max?: number
  step?: number
  placeholder?: string
  className?: string
}) {
  const shown = typeof value === 'number' ? String(value) : ''
  const [text, setText] = useState(shown)
  const last = useRef(shown)
  // Follow outside changes (JSON tab, undo) without fighting a half-typed number.
  useEffect(() => {
    if (shown !== last.current) {
      last.current = shown
      setText(shown)
    }
  }, [shown])
  return (
    <Input
      id={ctrl.id}
      fieldSize="sm"
      type="number"
      inputMode="decimal"
      min={min}
      max={max}
      step={step ?? 'any'}
      value={text}
      placeholder={placeholder}
      aria-invalid={ctrl.invalid || undefined}
      aria-describedby={ctrl.describedBy}
      className={cn('tabular-nums', className)}
      onChange={(e) => {
        const t = e.target.value
        setText(t)
        if (t.trim() === '') {
          last.current = ''
          onChange(undefined)
          return
        }
        const n = Number(t)
        if (Number.isFinite(n)) {
          last.current = String(n)
          onChange(n)
        }
      }}
    />
  )
}

export interface Option {
  value: string
  label: string
}

/** A native select; a current value that is not an option is kept (and shown) instead of lost. */
export function SelectInput({ ctrl, value, options, onChange, empty }: { ctrl: ControlProps; value: string | undefined; options: Option[]; onChange(v: string | undefined): void; empty?: string }) {
  const all = value && !options.some((o) => o.value === value) ? [...options, { value, label: value }] : options
  return (
    <select
      id={ctrl.id}
      className={SELECT_CLASS}
      value={value ?? ''}
      aria-invalid={ctrl.invalid || undefined}
      aria-describedby={ctrl.describedBy}
      onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
    >
      {empty !== undefined || !value ? <option value="">{empty ?? 'Choose…'}</option> : null}
      {all.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/**
 * A text box with suggestions (a native combobox: `<input list>`). `listId` points at a list the
 * editor rendered once for every field (then `suggestions` is not needed).
 */
export function ComboInput({
  ctrl,
  value,
  onChange,
  suggestions = [],
  listId: shared,
  placeholder,
  mono = true,
  normalize,
}: {
  ctrl: ControlProps
  value: unknown
  onChange(v: string | undefined): void
  suggestions?: Option[] | string[]
  listId?: string
  placeholder?: string
  mono?: boolean
  normalize?(raw: string): string
}) {
  const ownId = useId()
  const listId = shared ?? ownId
  const opts: Option[] = shared ? [] : (suggestions as Array<Option | string>).map((s) => (typeof s === 'string' ? { value: s, label: s } : s))
  return (
    <>
      <Input
        id={ctrl.id}
        fieldSize="sm"
        list={listId}
        value={typeof value === 'string' ? value : value == null ? '' : String(value)}
        placeholder={placeholder}
        aria-invalid={ctrl.invalid || undefined}
        aria-describedby={ctrl.describedBy}
        className={cn(mono && 'font-mono')}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          const v = normalize ? normalize(e.target.value) : e.target.value.trim()
          onChange(v === '' ? undefined : v)
        }}
      />
      {shared ? null : <OptionList id={listId} options={opts} />}
    </>
  )
}

/** A `<datalist>`; an option's label is shown only when it says more than its value. */
export function OptionList({ id, options }: { id: string; options: readonly Option[] }) {
  return (
    <datalist id={id}>
      {options.map((o) => (
        <option key={o.value} value={o.value} label={o.label !== o.value ? o.label : undefined} />
      ))}
    </datalist>
  )
}

/** A name as the registry spells it: lower case, `_` for spaces. */
export const nameNormalize = (raw: string): string => raw.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')

/**
 * A name that other parts of the document point at (a role, a move of the clip table): typed
 * freely, used when you leave the box or press Enter — and only when `accept` says it is fine
 * (otherwise the old name comes back and `rejectText` says why).
 */
export function CommitInput({
  label,
  value,
  onCommit,
  accept = () => true,
  normalize = nameNormalize,
  suggestions,
  rejectText,
  className,
  testId,
}: {
  label: string
  value: string
  onCommit(next: string): void
  accept?(next: string): boolean
  normalize?(raw: string): string
  suggestions?: readonly string[]
  rejectText?: string
  className?: string
  testId?: string
}) {
  const id = useId()
  const listId = useId()
  const [text, setText] = useState(value)
  const [rejected, setRejected] = useState(false)
  useEffect(() => setText(value), [value])
  const commit = (): void => {
    const next = normalize(text)
    if (next === value) {
      setText(value)
      setRejected(false)
      return
    }
    if (!next || !accept(next)) {
      setRejected(true)
      setText(value)
      return
    }
    setRejected(false)
    onCommit(next)
  }
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Input
        id={id}
        fieldSize="sm"
        list={suggestions ? listId : undefined}
        value={text}
        className="font-mono"
        autoComplete="off"
        spellCheck={false}
        data-testid={testId}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            setText(value)
          }
        }}
      />
      {suggestions ? (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      ) : null}
      {rejected ? (
        <p className="text-xs text-warn" role="status">
          {rejectText ?? 'That name can’t be used here; the old one is kept.'}
        </p>
      ) : null}
    </div>
  )
}

/** A place / participant ref with suggestions for every role, part and spot, read back in words. */
export function RefInput({ ctrl, value, onChange, faces = false, roleOnly = false, placeholder }: { ctrl: ControlProps; value: unknown; onChange(v: string | undefined): void; faces?: boolean; roleOnly?: boolean; placeholder?: string }) {
  const { roles, meta, lists } = useEditor()
  const shared = lists ? (faces ? lists.refsWithFaces : roleOnly ? lists.roleRefs : lists.refs) : undefined
  const options = useMemo(() => (shared ? [] : refOptions(roles, meta.sockets, { faces, roleOnly })), [shared, roles, meta.sockets, faces, roleOnly])
  const reading = typeof value === 'string' && value ? (['left', 'right', 'camera'].includes(value) ? `facing ${value}` : describeRef(value)) : null
  return (
    <div className="flex flex-col gap-1">
      <ComboInput ctrl={ctrl} value={value} onChange={onChange} suggestions={options} listId={shared} placeholder={placeholder ?? (roles[0] ? `$${roles[0]}` : 'spot:middle')} />
      {reading ? <p className="text-xs text-ink-3">= {reading}</p> : null}
    </div>
  )
}

/** A `role:event` ref (`thrower:release`, `ball:arrive`). */
export function EventRefInput({ ctrl, value, onChange }: { ctrl: ControlProps; value: unknown; onChange(v: string | undefined): void }) {
  const { roles, lists, meta } = useEditor()
  const options = useMemo(() => (lists ? [] : eventOptions(roles, meta.moments)), [lists, roles, meta.moments])
  return <ComboInput ctrl={ctrl} value={value} onChange={onChange} suggestions={options} listId={lists?.events} placeholder={roles[0] ? `${roles[0]}:release` : 'actor:release'} />
}

/**
 * A state value: `true` / `false` / numbers / refs / words (typed as the parent means them).
 * `noneIsNull` (a result's value): "none" is saved as `null` — "no one / nothing" — and `null`
 * shows as "none"; a check reads the word "none" that way by itself, a result would keep the word.
 */
export function ValueInput({ ctrl, value, onChange, stateKey, noneIsNull = false }: { ctrl: ControlProps; value: unknown; onChange(v: unknown): void; stateKey?: string; noneIsNull?: boolean }) {
  const { roles } = useEditor()
  const options = useMemo(() => valueOptions(roles, stateKey), [roles, stateKey])
  const listId = useId()
  return (
    <>
      <Input
        id={ctrl.id}
        fieldSize="sm"
        list={listId}
        value={noneIsNull && value === null ? 'none' : formatValue(value)}
        aria-invalid={ctrl.invalid || undefined}
        aria-describedby={ctrl.describedBy}
        className="font-mono"
        autoComplete="off"
        spellCheck={false}
        placeholder="true, happy, $friend…"
        onChange={(e) => onChange(noneIsNull && e.target.value.trim() === 'none' ? null : parseValue(e.target.value))}
      />
      <OptionList id={listId} options={options} />
    </>
  )
}

/**
 * A list of names as removable chips plus a box to add more (Enter, comma or leaving the box adds;
 * Backspace in an empty box removes the last one). `ordered` numbers them (a fallback ladder).
 */
export function TagInput({
  ctrl,
  values,
  onChange,
  suggestions = [],
  labelOf,
  titleOf,
  placeholder,
  ordered = false,
  normalize = nameNormalize,
  listId: shared,
}: {
  ctrl: ControlProps
  values: string[]
  onChange(next: string[]): void
  suggestions?: readonly string[]
  labelOf?(v: string): string
  titleOf?(v: string): string | undefined
  placeholder?: string
  ordered?: boolean
  normalize?(raw: string): string
  /** A list the editor rendered once (instead of `suggestions`). */
  listId?: string
}) {
  const [text, setText] = useState('')
  const ownId = useId()
  const listId = shared ?? ownId
  const add = (raw: string): void => {
    const items = raw
      .split(',')
      .map((s) => normalize(s.trim()))
      .filter((s) => s !== '' && !values.includes(s))
    if (items.length > 0) onChange([...values, ...new Set(items)])
    setText('')
  }
  const left = shared ? [] : suggestions.filter((s) => !values.includes(s))
  return (
    <div
      className={cn(
        'flex min-h-9 min-w-0 flex-wrap items-center gap-1 rounded-xl border border-line bg-paper px-1.5 py-1 hover:border-line-strong focus-within:focus-ring',
        ctrl.invalid && 'border-danger',
      )}
    >
      {values.map((v, i) => (
        <span key={v} className="inline-flex h-7 min-w-0 max-w-full items-center gap-1 rounded-full bg-paper-2 pl-2.5 pr-1 text-xs font-medium text-ink" title={titleOf?.(v)} data-testid="tag">
          {ordered ? <span className="text-ink-3">{i + 1}.</span> : null}
          <span className="truncate">{labelOf ? labelOf(v) : v}</span>
          <button type="button" className="inline-flex size-5 items-center justify-center rounded-full text-ink-3 hover:bg-paper-3 hover:text-ink focus-visible:focus-ring" aria-label={`Remove ${labelOf ? labelOf(v) : v}`} onClick={() => onChange(values.filter((x) => x !== v))}>
            <X className="size-3.5" aria-hidden />
          </button>
        </span>
      ))}
      <input
        id={ctrl.id}
        list={listId}
        value={text}
        placeholder={values.length === 0 ? placeholder : undefined}
        aria-invalid={ctrl.invalid || undefined}
        aria-describedby={ctrl.describedBy}
        className="h-7 min-w-24 flex-1 bg-transparent px-1 text-sm text-ink outline-none placeholder:text-ink-3"
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          const t = e.target.value
          if (t.includes(',')) add(t)
          else setText(t)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add(text)
          } else if (e.key === 'Backspace' && text === '' && values.length > 0) {
            onChange(values.slice(0, -1))
          }
        }}
        onBlur={() => add(text)}
      />
      {shared ? null : (
        <datalist id={listId}>
          {left.map((s) => (
            <option key={s} value={s} label={titleOf?.(s)} />
          ))}
        </datalist>
      )}
    </div>
  )
}

/** Pick any of a fixed set by pressing chips. */
type ChipOption = Option & { icon?: ReactNode; title?: string }

export function ChipPicker({ values, options, onChange, label }: { values: string[]; options: ChipOption[]; onChange(next: string[]): void; label: string }) {
  const all: ChipOption[] = [...options, ...values.filter((v) => !options.some((o) => o.value === v)).map((v) => ({ value: v, label: v }))]
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
      {all.map((o) => {
        const on = values.includes(o.value)
        return (
          <Chip key={o.value} size="sm" selected={on} icon={o.icon} title={o.title} onClick={() => onChange(on ? values.filter((v) => v !== o.value) : [...values, o.value])}>
            {o.label}
          </Chip>
        )
      })}
    </div>
  )
}

/** A yes/no line with a checkbox. */
export function CheckLine({ path, label, hint, checked, onChange, deep = true }: { path: string; label: string; hint?: string; checked: boolean; onChange(v: boolean): void; deep?: boolean }) {
  const id = useId()
  const { errors } = useIssues(path, deep)
  return (
    <div className="flex flex-col gap-0.5" data-field-path={path}>
      <label htmlFor={id} className="inline-flex items-center gap-2 text-sm text-ink">
        <input id={id} type="checkbox" className="size-4 accent-[var(--color-accent)]" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-invalid={errors.length > 0 || undefined} />
        {label}
      </label>
      {hint ? <p className="pl-6 text-xs text-ink-3">{hint}</p> : null}
      <IssueLines path={path} deep={deep} className="pl-6" />
    </div>
  )
}

/**
 * The checker's problems (red) and hints (amber) found at `path` (and under it, when `deep`), for a
 * part of the form that has no single control of its own (a group of pickers, a switch, a table).
 */
export function IssueLines({ path, deep = false, className }: { path: string; deep?: boolean; className?: string }) {
  const { errors, warnings } = useIssues(path, deep)
  if (errors.length === 0 && warnings.length === 0) return null
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {errors.map((e, i) => (
        <p key={`e${i}:${e.message}`} className="text-xs text-danger" data-testid="field-error" data-path={e.path}>
          {e.message}
        </p>
      ))}
      {warnings.map((w, i) => (
        <p key={`w${i}:${w.message}`} className="text-xs text-warn" data-testid="field-warning" data-path={w.path}>
          {w.message}
        </p>
      ))}
    </div>
  )
}
